import { useFormik } from 'formik';
import {
  ChangeEvent, FormEvent, useEffect, useMemo, useRef, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';
import useGetDrivers from '../../../../API/hooks/drivers/useGetDrivers';
import useGetDeviceLocations from '../../../../API/hooks/maps/useGetDeviceLocations';
import useGetVehicles from '../../../../API/hooks/vehicles/useGetVehicles';
import useGetDriversCount from '../../../../API/hooks/drivers/useGetDriversCount';
import useGetVehiclesStatus from '../../../../API/hooks/vehicles/useGetVehiclesStatus';
import useGetDeviceByDeviceId from '../../../../API/hooks/devices/useGetDeviceByDeviceId';
import usePostVideoRequest from '../../../../API/hooks/videos/usePostRequestVideo';
import useGetActiveCustomVodLimit from '../../../../API/hooks/videos/useGetActiveCustomVodLimit';
import useGetVideoTimelines from '../../../../API/hooks/videos/useGetVideoTimelines';
import useGetVideos from '../../../../API/hooks/videos/useGetVideos';
import useGetCurrentUser from '../../../../API/hooks/users/useGetCurrentUser';
import { PostVideoRequest, Video, VideoTimeline } from '../../../../API/types/Videos';
import { DriverDetails as DriverType } from '../../../../API/types/Drivers';
import { VehicleDetails as VehicleType } from '../../../../API/types/Vehicles';
import { DriverAndVehicle } from '../../types';
import Input from '../../../../components/common/Input/Input';
import { Button } from '../../../../components/ui/Button/Button';
import Modal from '../../../../components/common/Modal/Modal';
import MapWidget from '../../../../components/common/Map/Map';
import SearchEntity from '../SearchEntity/SearchEntity';
import S from './RequestVideoStyles';
import useVehicleThumbnail from '../../../../theme/useVehicleThumbnail';
import useDriverThumbnail from '../../../../theme/useDriverThumbnail';
import RangeSlider from './RangeSlider';
import EntityPicker from './EntityPicker';
import {
  dateValidation, timeValidation, durationValidation,
  parseToDateString, generateDateFromString,
  parseDateFromString, getTimeRanges, getTimeRangesById, parseToDateTimeString, formatNoFootageAvailableError,
} from './helpers';
import {
  intersectTimeRange,
  TimeRangeMs,
  OFFLINE_VOD_SUCCESS_MESSAGE,
  OFFLINE_VOD_TIMELINE_HINT_LINES,
} from '../../../../utils/videoFulfillmentUtils';
import { getSimpleEqQuery } from '../../../../utils/queryFilters';
import { getEndOfTheDay, getStartOfTheDay } from '../../../../utils/dateUtils';
import CreateNote from '../../../../components/Notes/CreateNote/CreateNote';
import ConfirmModal from '../../../../components/common/ConfirmModal/ConfirmModal';
import {
  formatCustomVodLimitMessage,
  MAX_CUSTOM_VOD_REQUESTS_PER_DEVICE,
} from '../../../../constants/customVod';

const DRIVER = 'driver';
const VEHICLE = 'vehicle';

type Props = {
  fleetId: string;
  closeModal: () => void;
};

function RequestVideo({ fleetId, closeModal }: Props): JSX.Element {
  const navigate = useNavigate();
  const { user } = useAuthenticator();
  const { data: currentUser } = useGetCurrentUser(fleetId, user.attributes?.email);
  const vehicleThumbnail = useVehicleThumbnail();
  const driverThumbnail = useDriverThumbnail();
  const { data: vehiclesCount } = useGetVehiclesStatus(fleetId);
  const { data: driversCount } = useGetDriversCount(fleetId);

  const [submitError, setSubmitError] = useState<string | string[] | null>(null);
  const [submitInfo, setSubmitInfo] = useState<string | null>(null);
  const [chooseEntityModalOpen, toggleChooseEntityModal] = useState(false);
  const [periodSelection, togglePeriodSelection] = useState(true);
  const [addNote1ModalOpen, toggleAddNote1Modal] = useState(false);
  const [addNote2ModalOpen, toggleAddNote2Modal] = useState(false);
  const [existedVideoModalOpen, toggleExistedVideoModalOpen] = useState<string | null>(null);
  const [requestedVideo1, setRequestedVideo1Id] = useState<Video | null>(null);
  const [requestedVideo2, setRequestedVideo2Id] = useState<Video | null>(null);
  const [entityName, setEntityName] = useState<string | null>(null);
  const [driver, setDriver] = useState<DriverType | null>(null);
  const [vehicle, setVehicle] = useState<VehicleType | null>(null);
  const currentTimeRangesRef = useRef<TimeRangeMs[]>([]);
  const deviceId = driver?.device?.deviceId
    || driver?.deviceId
    || vehicle?.device?.deviceId
    || vehicle?.deviceId;
  const { data: liveDevice } = useGetDeviceByDeviceId(deviceId as string, fleetId, !!deviceId);
  const device = liveDevice ?? driver?.device ?? vehicle?.device;
  const isDeviceOffline = !!deviceId && (
    liveDevice?.isOnline === false
    || (liveDevice === undefined && device?.isOnline === false)
  );
  const { data: activeCustomVodLimit } = useGetActiveCustomVodLimit(
    fleetId,
    deviceId,
    !!deviceId && isDeviceOffline,
  );
  const activeCustomVodCount = activeCustomVodLimit?.count ?? 0;
  const remainingCustomVodSlots = activeCustomVodLimit?.remaining
    ?? MAX_CUSTOM_VOD_REQUESTS_PER_DEVICE;

  const onSubmitSuccess = (videos: Video[]): void => {
    const queued = videos.some((video) => video.fulfillmentStatus === 'queuedOffline');
    const partial = videos.some((video) => video.fulfillmentStatus === 'partial');
    if (queued) {
      setSubmitInfo(OFFLINE_VOD_SUCCESS_MESSAGE);
    } else if (partial) {
      setSubmitInfo('VoD request submitted with partial footage — only the available portion of your selected time range will be uploaded.');
    }
    setRequestedVideo1Id(videos[0]);
    setRequestedVideo2Id(videos[1]);
    toggleAddNote1Modal(true);
    if (videos[1]) {
      toggleAddNote2Modal(true);
    }
  };

  const closeModalAction = (): void => {
    if (!addNote1ModalOpen || !addNote2ModalOpen) {
      closeModal();
    }
  };

  const onSubmitFail = (errorMessage: string): void => setSubmitError(errorMessage);

  const { mutate } = usePostVideoRequest(onSubmitSuccess, onSubmitFail);

  const deSelectEntity = (): void => setEntityName(null);

  const closeEntityModal = (nochanges: boolean = true): void => {
    if (nochanges) {
      if (!driver && !vehicle) {
        deSelectEntity();
      } else if (entityName === DRIVER && !driver && vehicle) {
        setEntityName('vehicle');
      } else if (entityName === VEHICLE && driver && !vehicle) {
        setEntityName('driver');
      }
    }
  };

  const addSecondsToISOString = (dateISOString: any, seconds: number):any => {
    const date = new Date(dateISOString);
    date.setSeconds(date.getSeconds() + seconds);
    return date.toISOString();
  };

  const formik = useFormik({
    initialValues: {
      date: parseToDateString(getStartOfTheDay()),
      from: '',
      to: '',
      duration: '30',
      cameraId: '1',
    },
    onSubmit: (values) => {
      const requestedCameraCount = values.cameraId === '0' ? 2 : 1;
      if (
        isDeviceOffline
        && activeCustomVodCount + requestedCameraCount > MAX_CUSTOM_VOD_REQUESTS_PER_DEVICE
      ) {
        setSubmitError(
          formatCustomVodLimitMessage(
            activeCustomVodCount,
            remainingCustomVodSlots,
            requestedCameraCount,
          ),
        );
        formik.setSubmitting(false);
        return;
      }

      const requestedFromIso = generateDateFromString(values.date, values.from);
      const requestedToIso = periodSelection
        ? generateDateFromString(values.date, values.to)
        : addSecondsToISOString(generateDateFromString(values.date, values.from), +values.duration);

      let fromIso = requestedFromIso;
      let toIso = requestedToIso;
      let fulfillmentStatus: PostVideoRequest['fulfillmentStatus'] = 'full';

      if (isDeviceOffline) {
        fulfillmentStatus = 'queuedOffline';
      } else if (currentTimeRangesRef.current.length) {
        const requestedFromMs = new Date(requestedFromIso).getTime();
        const requestedToMs = new Date(requestedToIso).getTime();
        const clip = intersectTimeRange(requestedFromMs, requestedToMs, currentTimeRangesRef.current);
        if (!clip) {
          setSubmitError(formatNoFootageAvailableError(currentTimeRangesRef.current));
          formik.setSubmitting(false);
          return;
        }
        fromIso = new Date(clip.from).toISOString();
        toIso = new Date(clip.to).toISOString();
        fulfillmentStatus = clip.isPartial ? 'partial' : 'full';
        if (clip.isPartial) {
          setSubmitInfo('Only part of your selected range is available on the device. The request will use the available footage.');
        }
      }

      if (!currentUser?._id) {
        setSubmitError('Unable to identify the current user. Please refresh and try again.');
        formik.setSubmitting(false);
        return;
      }

      if (!deviceId) {
        setSubmitError('Select a driver or vehicle to request VoD.');
        formik.setSubmitting(false);
        return;
      }

      mutate({
        from: fromIso,
        to: toIso,
        requestedFrom: requestedFromIso,
        requestedTo: requestedToIso,
        fulfillmentStatus,
        cameraIds: values.cameraId === '0' ? [1, 2] : [Number(values.cameraId)],
        fleetId,
        deviceId,
        requestedByUserId: currentUser._id,
      });
    },
    validate: (values) => {
      const errors = {} as Record<string, string | undefined>;

      if (!values.date.length) errors.date = 'Required';
      if (values.date && !dateValidation.test(values.date)) {
        errors.date = 'Wrong format dd/mm/yyyy';
      }
      if (!values.from.length) errors.from = 'Required';
      if (values.from && !timeValidation.test(values.from)) {
        errors.from = 'Wrong format hh:mm:ss';
      }
      if (!values.to.length && periodSelection) errors.to = 'Required';
      if (values.to && !timeValidation.test(values.to)) {
        errors.to = 'Wrong format hh:mm:ss';
      }
      if (!values.duration.length && !periodSelection) errors.duration = 'Required';
      if (values.duration && !durationValidation.test(values.duration)) {
        errors.duration = 'Should be a number';
      }
      if (!periodSelection && values.duration && values.from && timeValidation.test(values.from) && !isDeviceOffline) {
        const fromTime = new Date(generateDateFromString(values.date, values.from)).getTime();
        const toTime = fromTime + Number(values.duration) * 1000;
        const inSlot = currentTimeRangesRef.current.some(([s, e]) => fromTime >= s && toTime <= e);
        if (!inSlot) errors.duration = 'Selected range is outside available timeline';
      }
      if (!values.cameraId) errors.cameraId = 'Required';

      return errors;
    },
    validateOnMount: true,
  });

  const { day, month, year } = parseDateFromString(formik.values.date);
  const currentDate = new Date(+year, +month - 1, +day);
  const isValidDate = !Number.isNaN(currentDate?.getTime());

  const { data: videos } = useGetVideos(
    fleetId,
    `?limit=100&skip=0&sortField=createdAt&sortOrder=DESC${formik.values.cameraId !== '0' ? getSimpleEqQuery('cameraId', formik.values.cameraId, 6) : ''}${getSimpleEqQuery('deviceId', deviceId, 1)}&from=${isValidDate ? currentDate?.toISOString() : ''}&to=${isValidDate ? addSecondsToISOString(currentDate?.toISOString(), 86400) : ''}`,
    !!deviceId && isValidDate,
  );
  const existedVideos = videos?.results
    ?.filter((video) => video.status !== 'failed')
    .map((video) => ({ id: video._id, start: video.from || video.fromTimestamp, end: video.to || video.toTimestamp })) || [];
  const isDeviceOnline = !!deviceId && !isDeviceOffline;
  const { data: timelines, isError: isTimelinesError } = useGetVideoTimelines(
    { fleetId, deviceId, date: currentDate },
    { isOnline: isDeviceOnline },
  );
  const timeRanges = deviceId ? getTimeRanges(timelines as VideoTimeline) : {};
  const currentTimeRanges = useMemo(
    () => getTimeRangesById(timeRanges, formik.values.cameraId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [timelines, formik.values.cameraId],
  );
  currentTimeRangesRef.current = currentTimeRanges;

  const timelineErrorMessage = ((): string => {
    if (isDeviceOffline) {
      return '';
    }
    if (isTimelinesError) {
      return 'Timelines are not available for this date.';
    }
    return '';
  })();

  const fullDayTimeRange = useMemo((): TimeRangeMs[] => {
    const dayStart = currentDate.getTime();
    return [[dayStart, dayStart + 86399999]];
  }, [currentDate]);

  let sliderTimeRanges = currentTimeRanges;
  if (isDeviceOffline) {
    sliderTimeRanges = currentTimeRanges.length ? currentTimeRanges : fullDayTimeRange;
  }

  const getVehicleSubtitle = (selectedVehicle: VehicleType | null): string => {
    if (!selectedVehicle) {
      return '';
    }
    const manufacturer = selectedVehicle.manufacturer ? `${selectedVehicle.manufacturer} ` : '';
    const model = selectedVehicle.model ? `${selectedVehicle.model} ` : '';
    return `${manufacturer}${model}`;
  };

  const isTimeRangeValid = useMemo(() => {
    const {
      date, from, to, duration,
    } = formik.values;
    if (!date || !dateValidation.test(date)) {
      return false;
    }
    if (!from || !timeValidation.test(from)) {
      return false;
    }
    if (periodSelection) {
      return !!to && timeValidation.test(to);
    }
    return !!duration && durationValidation.test(duration);
  }, [formik.values, periodSelection]);

  const camerasToRequest = formik.values.cameraId === '0' ? 2 : 1;
  const customVodLimitMessage = isDeviceOffline && deviceId
    ? formatCustomVodLimitMessage(activeCustomVodCount, remainingCustomVodSlots, camerasToRequest)
    : '';
  const exceedsCustomVodLimit = isDeviceOffline && !!customVodLimitMessage;

  const canSubmitRequest = !!deviceId
    && !!currentUser?._id
    && isTimeRangeValid
    && !formik.isSubmitting
    && (isDeviceOffline ? !exceedsCustomVodLimit : true);

  const renderSubmitError = (): JSX.Element | null => {
    if (!submitError) {
      return null;
    }
    if (typeof submitError === 'string') {
      return <S.ErrorMessage>{submitError}</S.ErrorMessage>;
    }
    return (
      <>
        {submitError.map((error) => (
          <S.ErrorMessage key={error}>{error}</S.ErrorMessage>
        ))}
      </>
    );
  };

  // if there is onSubmit error formik will stack in submitting state
  // blocking further interactions with submit button
  useEffect(() => {
    formik.setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitError]);

  useEffect(() => {
    if (!deviceId) {
      return;
    }
    if (isDeviceOffline) {
      setSubmitError('');
    }
    formik.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, isDeviceOffline]);

  const handleInputChange = (e: ChangeEvent<any>): void => {
    formik.handleChange(e);
    setSubmitError('');
    if (!isDeviceOffline) {
      setSubmitInfo('');
    }
  };

  const handlePeriodSelectionChange = (e: ChangeEvent<any>): void => {
    togglePeriodSelection(e.target.value === 'period');
    setSubmitError('');
    if (!isDeviceOffline) {
      setSubmitInfo('');
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const dateFrom = new Date(generateDateFromString(formik.values.date, formik.values.from));
    const dateTo = periodSelection
      ? new Date(generateDateFromString(formik.values.date, formik.values.to))
      : new Date(dateFrom.getTime() + (+formik.values.duration * 1000));

    const existedVideo = existedVideos.find((video) => dateFrom >= new Date(video.start) && dateTo <= new Date(video.end));
    if (existedVideo) {
      setSubmitError('Video already exists for this time range');
      toggleExistedVideoModalOpen(existedVideo?.id);
      formik.setSubmitting(false);
      return;
    }

    if (isDeviceOffline) {
      formik.handleSubmit(e);
      return;
    }

    if (!currentTimeRanges.length) {
      setSubmitError('No timelines available for this date. Try another date or request while the device is offline.');
      formik.setSubmitting(false);
      return;
    }

    formik.handleSubmit(e);
  };

  const handleSetEntity = (data: DriverAndVehicle): void => {
    setSubmitError('');
    setSubmitInfo('');
    if (entityName === DRIVER && data) {
      setVehicle(null);
      setDriver(data);
    }
    if (entityName === VEHICLE && data) {
      setDriver(null);
      setVehicle(data);
    }
  };

  return (
    <>
      <S.Wrapper>
        <form onSubmit={handleSubmit}>
          <S.FormBody>
            <div className="details-container">
              <h4 className="form-title">Driver</h4>
              <EntityPicker
                thumbnail={driver?.avatarUrl || driverThumbnail}
                entity={DRIVER}
                title={`${driver?.firstName} ${driver?.lastName}`}
                subtitle={driver?.licenseName || ''}
                avatar={driver?.avatarUrl as string}
                isSelected={entityName === DRIVER}
                count={driversCount?.total || 0}
                toggleModal={toggleChooseEntityModal}
                onClick={setEntityName}
              />
              <h4 className="form-title">Vehicle</h4>
              <EntityPicker
                thumbnail={vehicleThumbnail}
                entity={VEHICLE}
                title={`${vehicle?.licensePlateNumber}`}
                subtitle={getVehicleSubtitle(vehicle)}
                isSelected={entityName === VEHICLE}
                count={vehiclesCount?.total || 0}
                toggleModal={toggleChooseEntityModal}
                onClick={setEntityName}
              />
              <S.TimeRangeSection>
                {isDeviceOffline && (
                  <S.OfflineTimelineSection>
                    <S.OfflineStatusTitle>Device offline</S.OfflineStatusTitle>
                    {OFFLINE_VOD_TIMELINE_HINT_LINES.map((line) => (
                      <S.OfflineStatusMessage key={line}>{line}</S.OfflineStatusMessage>
                    ))}
                    <S.OfflineStatusMessage>
                      Enter the time range below. The request will be queued until the device reconnects.
                    </S.OfflineStatusMessage>
                  </S.OfflineTimelineSection>
                )}
                <S.RowContainer>
                  <p>Select time range by:</p>
                  <input
                    type="radio"
                    id="periodChoice1"
                    value="period"
                    onChange={handlePeriodSelectionChange}
                    checked={periodSelection}
                  />
                  <label htmlFor="periodChoice1"><span className="checkbox" />Period</label>
                  <input
                    type="radio"
                    id="periodChoice2"
                    onChange={handlePeriodSelectionChange}
                    value="duration"
                    checked={!periodSelection}
                  />
                  <label htmlFor="periodChoice2"><span className="checkbox" />Duration</label>
                </S.RowContainer>
                <S.RowContainer>
                  <Input
                    label="Date"
                    name="date"
                    placeholder="22/10/2028"
                    onChange={handleInputChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.date}
                    error={formik.touched.date && formik.errors.date}
                  />
                  <Input
                    label="Start"
                    name="from"
                    placeholder="22:00:00"
                    onChange={handleInputChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.from}
                    error={formik.touched.from && formik.errors.from}
                  />
                  {periodSelection && (
                    <Input
                      label="End"
                      name="to"
                      placeholder="23:30:15"
                      onChange={handleInputChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.to}
                      error={formik.touched.to && formik.errors.to}
                    />
                  )}
                  {!periodSelection && (
                    <Input
                      label="Duration (sec)"
                      name="duration"
                      placeholder="30"
                      onChange={handleInputChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.duration}
                      error={formik.touched.duration && formik.errors.duration}
                    />
                  )}
                </S.RowContainer>
                <RangeSlider
                  currentDate={currentDate}
                  timeRanges={sliderTimeRanges}
                  errorMsg={timelineErrorMessage || undefined}
                  periodSelection={periodSelection}
                  duration={Number(formik.values.duration) || 0}
                  onRangeSelect={(from, to) => {
                    if (periodSelection) {
                      formik.setValues({ ...formik.values, from, to }, true);
                    } else {
                      formik.setValues({ ...formik.values, from }, true);
                    }
                  }}
                />
              </S.TimeRangeSection>
              <S.RowContainer>
                <p>Camera:</p>
                <input
                  type="radio"
                  id="cameraChoice1"
                  name="cameraId"
                  value="1"
                  onChange={handleInputChange}
                  checked={formik.values.cameraId === '1'}
                />
                <label htmlFor="cameraChoice1"><span className="checkbox" />Outward</label>
                <input
                  type="radio"
                  id="cameraChoice2"
                  name="cameraId"
                  onChange={handleInputChange}
                  value="2"
                />
                <label htmlFor="cameraChoice2"><span className="checkbox" />Inward</label>
                <input
                  type="radio"
                  id="cameraChoice3"
                  name="cameraId"
                  onChange={handleInputChange}
                  value="0"
                />
                <label htmlFor="cameraChoice3"><span className="checkbox" />Both</label>
              </S.RowContainer>
            </div>
            <div className="map-container">
              <div className="map-card">
                <MapWidget
                  fleetId={fleetId}
                  getLocationsQuery={useGetDeviceLocations}
                  entityId={deviceId as string}
                  entityType="video"
                  isTrip={false}
                />
              </div>
            </div>
          </S.FormBody>
          <S.FormFooter>
            <S.MessagesContainer>
              {!!submitInfo && <S.InfoMessage>{submitInfo}</S.InfoMessage>}
              {!deviceId && (
                <S.InfoMessage>Select a driver or vehicle to request VoD.</S.InfoMessage>
              )}
              {!!customVodLimitMessage && (
                <S.ErrorMessage>{customVodLimitMessage}</S.ErrorMessage>
              )}
              {renderSubmitError()}
            </S.MessagesContainer>
            <S.FormFooterActions>
              <Button
                height="50px"
                onClick={closeModal}
                type="button"
              >
                Cancel
              </Button>
              <Button
                className="request-button"
                height="50px"
                type="submit"
                disabled={!canSubmitRequest}
              >
                Request VOD
              </Button>
            </S.FormFooterActions>
          </S.FormFooter>
        </form>
      </S.Wrapper>
      {chooseEntityModalOpen && (
        <Modal
          title={`Select ${entityName}`}
          state={chooseEntityModalOpen}
          toggleState={toggleChooseEntityModal}
          onClose={closeEntityModal}
        >
          <SearchEntity
            fleetId={fleetId}
            entity={entityName as string}
            onSubmit={() => {
              closeEntityModal(false);
              toggleChooseEntityModal(false);
            }}
            onCancel={() => {
              closeEntityModal();
              toggleChooseEntityModal(false);
            }}
            getQuery={entityName === DRIVER ? useGetDrivers : useGetVehicles}
            setEntity={handleSetEntity}
          />
        </Modal>
      )}
      {addNote1ModalOpen && (
        <Modal
          title={`Add note for requested VoD (${requestedVideo1?.cameraId === 1 ? 'Outward' : 'Inward'} camera)`}
          state={addNote1ModalOpen}
          toggleState={toggleAddNote1Modal}
          onClose={() => closeModalAction()}
        >
          <CreateNote fleetId={fleetId} entityName="videos" entityId={requestedVideo1?._id || ''} toggleModal={toggleAddNote1Modal} onClose={closeModalAction} />
        </Modal>
      )}
      {addNote2ModalOpen && (
        <Modal
          title={`Add note for requested VoD (${requestedVideo2?.cameraId === 1 ? 'Outward' : 'Inward'} camera)`}
          state={addNote2ModalOpen}
          toggleState={toggleAddNote2Modal}
          onClose={() => closeModalAction()}
        >
          <CreateNote fleetId={fleetId} entityName="videos" entityId={requestedVideo2?._id || ''} toggleModal={toggleAddNote2Modal} onClose={closeModalAction} />
        </Modal>
      )}
      {!!existedVideoModalOpen && (
        <Modal
          title="Video in this time range is already exists"
          state={!!existedVideoModalOpen}
          toggleState={() => toggleExistedVideoModalOpen(null)}
          onClose={() => toggleExistedVideoModalOpen(null)}
        >
          <ConfirmModal
            title="Would you like to open existed video?"
            onConfirm={() => {
              navigate(`/videos/${existedVideoModalOpen}`);
            }}
            onClose={() => toggleExistedVideoModalOpen(null)}
          />
        </Modal>
      )}
    </>
  );
}

export default RequestVideo;
